/**
 * Integrations transport (#98): the organization's Nextcloud connection through
 * the TYPO3 proxy (type=3006). Proxy mode only. Same rules as every proxy call:
 * CSRF header, session cookie, session loss → pending, one CSRF refresh + retry.
 * TYPO3 holds the Nextcloud credential and decides who may do what; this class
 * only sends and parses (docs/nextcloud-connection-design.md).
 */

import { validateGatewayUrl } from './app-constants';
import {
  assertAuthorized,
  buildProxyHeaders,
  getRequiredCsrfToken,
  withCsrfRetry,
  type AuthService,
} from './auth-utils';
import { GatewayError, GatewayErrorCode } from './gateway/error-codes';
import type { FileItem, FolderItem, FolderListing } from './files-service';
import { duplicateNameError } from './gateway/proxy-post';
import type { CheckRulesResponse } from './gateway-service';
import { pendingIfSessionEnded, suspendIfSessionEnded } from './session-ended';

export type NextcloudState = 'none' | 'pending' | 'active' | 'needsReconnect';

export interface ExposedFolder {
  id: string;
  name: string;
  path: string;
  /** false: renamed, moved, deleted or unshared in Nextcloud (last check) */
  available: boolean;
}

export interface NextcloudStatus {
  state: NextcloudState;
  /** owner/admin */
  canManage: boolean;
  /** the admin who connected, connection active */
  canPick: boolean;
  // Only for owners/admins:
  server?: string;
  account?: { loginName: string; displayName: string };
  connectedBy?: { name: string } | null;
  connectedAt?: string;
  lastCheckedAt?: string | null;
  folders?: ExposedFolder[];
}

export interface NextcloudBrowseFolder {
  name: string;
  path: string;
  mountType: 'own' | 'shared' | 'group' | 'external';
  exposed: boolean;
  canExpose: boolean;
}

export interface NextcloudBrowse {
  /** '' = account root */
  path: string;
  /** breadcrumbs from the top down to the current folder (root excluded) */
  parents: { name: string; path: string }[];
  folders: NextcloudBrowseFolder[];
  truncated: boolean;
}

export interface ConnectStart {
  flowId: string;
  loginUrl: string;
  expiresAt: string;
  /** The name Nextcloud's approval page shows (the User-Agent TYPO3 sends). */
  appName?: string;
}

export type ConnectPoll = { state: 'waiting' | 'expired' } | NextcloudStatus;

/** The filtered Docling result of a stored analysis (the documents.result keys). */
export interface StoredParse {
  md_content?: string;
  json_content?: unknown;
  html_content?: string;
  html_split_page_content?: unknown;
  text_content?: string;
  doctags_content?: string;
}

/** #98 part 4: the newest finished analysis of a Nextcloud file (nextcloud.analysis). */
export interface StoredAnalysis {
  analysis: {
    analyzedAt: string | null;
    version: string;
    language: string;
    /** Made with an older analysis version than the current one. */
    outdated: boolean;
    /** The file changed in Nextcloud since (etag). */
    changed: boolean;
  };
  /** parse is null for plain-text formats (not run through Docling). */
  document: { text: string; parse: StoredParse | null };
  check: CheckRulesResponse;
}

export interface CollectionState {
  path: string;
  status: string;
  documentId: string | null;
  changed: boolean;
}

/** A coded proxy answer (400/409/422/429/502) the UI shows as a message. */
export class IntegrationError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'IntegrationError';
  }
}

export function isIntegrationError(error: unknown): error is IntegrationError {
  return error instanceof IntegrationError;
}

export default class IntegrationsService {
  constructor(
    private readonly options: { endpoint: string; authService: AuthService }
  ) {}

  status(): Promise<NextcloudStatus> {
    return this.command('nextcloud.status');
  }

  check(): Promise<NextcloudStatus> {
    return this.command('nextcloud.check');
  }

  connectStart(serverUrl: string): Promise<ConnectStart> {
    return this.command('nextcloud.connectStart', { serverUrl });
  }

  connectPoll(flowId: string): Promise<ConnectPoll> {
    return this.command('nextcloud.connectPoll', { flowId });
  }

  connectCancel(flowId?: string): Promise<NextcloudStatus> {
    return this.command('nextcloud.connectCancel', flowId ? { flowId } : {});
  }

  confirm(): Promise<NextcloudStatus> {
    return this.command('nextcloud.confirm');
  }

  browse(path: string): Promise<NextcloudBrowse> {
    return this.command('nextcloud.browse', { path });
  }

  addFolder(path: string): Promise<NextcloudStatus> {
    return this.command('nextcloud.addFolder', { path });
  }

  removeFolder(folderId: string): Promise<NextcloudStatus> {
    return this.command('nextcloud.removeFolder', { folderId });
  }

  disconnect(): Promise<NextcloudStatus> {
    return this.command('nextcloud.disconnect');
  }

  /** #98 part 3: the mount (no folderId) or a folder inside an exposed folder. */
  list(
    folderId: string | null = null,
    path = '',
    page = 1,
    pageSize = 25
  ): Promise<FolderListing> {
    return this.command('nextcloud.list', {
      ...(folderId ? { folderId } : {}),
      path,
      page,
      pageSize,
    });
  }

  async mkdir(
    folderId: string,
    path: string,
    name: string
  ): Promise<FolderItem> {
    const data = await this.command<{ item?: FolderItem }>('nextcloud.mkdir', {
      folderId,
      path,
      name,
    });
    if (!data.item) throw invalidResponse();
    return data.item;
  }

  /** #98 part 4: load one file into IMSight (analyze it); answers its row (state analyzing). */
  async analyze(folderId: string, path: string): Promise<FileItem> {
    const data = await this.command<{ item?: FileItem }>('nextcloud.analyze', {
      folderId,
      path,
    });
    if (!data.item) throw invalidResponse();
    return data.item;
  }

  /** #98 part 4: the stored analysis of one file (no analysis runs). */
  async analysis(folderId: string, path: string): Promise<StoredAnalysis> {
    const data = await this.command<Partial<StoredAnalysis>>(
      'nextcloud.analysis',
      { folderId, path }
    );
    // A check without its statements list would break the Reader.
    if (
      !data.analysis ||
      !data.document ||
      !Array.isArray(data.check?.statements)
    )
      throw invalidResponse();
    return data as StoredAnalysis;
  }

  /**
   * Add one Nextcloud file to the collection; answers its row (collection state in `collection`).
   * A name clash is the Files DUPLICATE_NAME error with `existing`; replaceId replaces that document.
   */
  async addToCollection(
    folderId: string,
    path: string,
    replaceId?: string
  ): Promise<FileItem> {
    const data = await this.command<{ item?: FileItem }>('nextcloud.add', {
      folderId,
      path,
      ...(replaceId ? { replaceId } : {}),
    });
    if (!data.item) throw invalidResponse();
    return data.item;
  }

  /** Collection state per path; a path missing from the answer is not added. */
  async collectionStates(
    folderId: string,
    paths: string[]
  ): Promise<CollectionState[]> {
    const data = await this.command<{ states?: CollectionState[] }>(
      'nextcloud.collectionStates',
      { folderId, paths }
    );
    return Array.isArray(data.states) ? data.states : [];
  }

  /** One authorized POST; answers `data`, throws typed errors. */
  private async command<T>(
    command: string,
    fields: Record<string, string | number | string[]> = {}
  ): Promise<T> {
    const pending = pendingIfSessionEnded<T>();
    if (pending) return pending;
    try {
      return await withCsrfRetry(this.options.authService, async () => {
        const csrf = getRequiredCsrfToken(this.options.authService);
        validateGatewayUrl(this.options.endpoint);
        const response = await fetch(this.options.endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: buildProxyHeaders(csrf),
          body: JSON.stringify({ command, ...fields }),
        });
        // FOLDER_UNAVAILABLE (#98 part 3) and NOT_ANALYZED (part 4) must stay apart from a plain 404.
        if (response.status === 404)
          await throwIfCoded(response, command, [
            'FOLDER_UNAVAILABLE',
            'NOT_ANALYZED',
          ]);
        await assertAuthorized(response);
        if (!response.ok) {
          await throwIfCoded(response, command);
          throw new GatewayError(
            `Integrations request failed: ${response.status}`,
            GatewayErrorCode.NETWORK_ERROR,
            { status: response.status }
          );
        }
        const data = ((await response.json()) as { data?: T } | null)?.data;
        if (data === undefined || data === null) {
          throw new GatewayError(
            'Invalid integrations response',
            GatewayErrorCode.INVALID_RESPONSE,
            { status: response.status }
          );
        }
        return data;
      });
    } catch (error) {
      const suspended = suspendIfSessionEnded<T>(error);
      if (suspended) return suspended;
      throw error;
    }
  }
}

function invalidResponse(): GatewayError {
  return new GatewayError(
    'Invalid integrations response',
    GatewayErrorCode.INVALID_RESPONSE,
    { status: 200 }
  );
}

/**
 * A non-OK answer whose JSON body carries a code (one of `only`, if given)
 * becomes an IntegrationError. Only nextcloud.add's DUPLICATE_NAME is the
 * Files duplicate error (with `existing`, for Replace); New folder keeps its
 * own "folder exists" text (#146).
 */
async function throwIfCoded(
  response: Response,
  command: string,
  only?: string[]
): Promise<void> {
  let body: { code?: unknown; error?: unknown } | null;
  try {
    body = (await response.json()) as typeof body;
  } catch {
    return;
  }
  if (typeof body?.code !== 'string') return;
  if (only && !only.includes(body.code)) return;
  if (body.code === 'DUPLICATE_NAME' && command === 'nextcloud.add') {
    throw duplicateNameError((body as { existing?: unknown }).existing);
  }
  throw new IntegrationError(
    typeof body.error === 'string' ? body.error : body.code,
    body.code,
    response.status
  );
}
